import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Modal,
  IconButton,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { investorApi } from "../api/api";
import { useSelector } from "react-redux";
import Loader from "../components/common/Loader";

const BASE_URL = "http://localhost/realmart_api/public";

const LegalTeam = () => {
  const [legalTeam, setLegalTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const theme = useSelector((data) => data.themeValue.colors);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchLegalTeam = async () => {
      try {
        const response = await investorApi.getLegalTeam();
        if (response.status === 200) {
          const updatedData = response.data.map((item) => ({
            ...item,
            profile_path: item.profile_path.startsWith("http")
              ? item.profile_path
              : `${BASE_URL}/storage/${item.profile_path.replace(
                  "public/storage/",
                  ""
                )}`,
          }));
          setLegalTeam(updatedData);
        }
      } catch (err) {
        console.error("API Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLegalTeam();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 6, minHeight: "100vh", py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h3"
          fontWeight="bold"
          gutterBottom
          sx={{
            color: theme.text,
            background: theme.mode === 'dark' 
              ? 'linear-gradient(45deg, #FFFFFF, #BB86FC)'
              : 'linear-gradient(45deg, #1976d2, #000000)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            fontSize: { xs: '2rem', md: '3rem' }
          }}
        >
          Our Legal Experts
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.text,
            opacity: 0.8,
            maxWidth: 600,
            margin: '0 auto',
          }}
        >
          Dedicated professionals committed to protecting your interests
        </Typography>
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { 
              xs: "1fr", 
              sm: "repeat(2, 1fr)", 
              lg: "repeat(2, 1fr)" 
            },
            gap: 4,
          }}
        >
          {legalTeam.map((member) => (
            <Paper
              key={member.id}
              elevation={8}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                backgroundColor: theme.cardBackground || theme.background,
                borderRadius: 4,
                border: `1px solid ${theme.border || 'rgba(255,255,255,0.1)'}`,
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                height: { xs: "auto", sm: 280 },
                position: "relative",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  borderColor: theme.primary_gradient_start_color,
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${theme.primary_gradient_start_color}, ${theme.primary_gradient_end_color || '#00c6ff'})`,
                  transform: "scaleX(0)",
                  transition: "transform 0.3s ease",
                },
                "&:hover::before": {
                  transform: "scaleX(1)",
                },
              }}
            >
              {/* Image Section */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "40%" },
                  height: { xs: 200, sm: "100%" },
                  overflow: "hidden",
                  position: "relative",
                  "&:hover img": { 
                    transform: "scale(1.1)" 
                  },
                }}
                onClick={() => setSelectedImage(member.profile_path)}
              >
                <img
                  src={member.profile_path}
                  alt={member.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.5s ease",
                  }}
                />
                {/* Overlay on hover */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(45deg, rgba(25,118,210,0.2), transparent)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: "bold",
                    }}
                  >
                    Click to View
                  </Typography>
                </Box>
              </Box>

              {/* Content Section */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "60%" },
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {/* Name */}
                <Typography 
                  variant="h5" 
                  fontWeight="700" 
                  sx={{ 
                    color: theme.text,
                    mb: 1,
                    lineHeight: 1.2,
                  }}
                >
                  {member.name}
                </Typography>

                {/* Specialization as Chip */}
                <Chip
                  label={member.specialization}
                  size="small"
                  sx={{
                    backgroundColor: theme.primary_gradient_start_color,
                    color: "white",
                    fontWeight: "600",
                    mb: 2,
                    fontSize: "0.75rem",
                    height: 26,
                    maxWidth: "fit-content",
                  }}
                />

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.text,
                    opacity: 0.9,
                    lineHeight: 1.6,
                    fontSize: "0.9rem",
                    display: "-webkit-box",
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {member.description}
                </Typography>

                {/* Read More Indicator */}
                {/* <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                    opacity: 0.7,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.primary_gradient_start_color,
                      fontWeight: "600",
                      fontSize: "0.8rem",
                    }}
                  >
                    Learn more about {member.name.split(' ')[0]}
                  </Typography>
                </Box> */}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Enhanced Modal */}
      <Modal 
        open={!!selectedImage} 
        onClose={() => setSelectedImage(null)}
        sx={{
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 4,
            p: 3,
            outline: "none",
            maxWidth: isMobile ? "95%" : 600,
            width: "auto",
            maxHeight: "90vh",
          }}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.7)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Profile Enlarged"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "12px",
              objectFit: "contain",
              maxHeight: "80vh",
            }}
          />
        </Box>
      </Modal>

      {/* Empty State */}
      {!loading && legalTeam.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h6" sx={{ color: theme.text, opacity: 0.7 }}>
            No legal team members found
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default LegalTeam;